import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button, Flex, Form, Input, Radio } from 'antd';
import FadeIn from '../FadeIn';
import useFeedbackLabel from '../../hooks/ui/useFeedbackLabel';
import useForm from '../../hooks/utility/useForm';
import useBusinessProfile from '../../hooks/context/useBusinessProfile';
import { BusinessProfileUpdateStrategy } from '../../providers/BusinessProfileProvider';
import AddressFields from '../form/address/AddressFields';
import { LegalForm } from '../../services/exports/Constants';

const LEGAL_FIELDS = [
  'legal_form',
  'legal_name',
  'legal_country',
  'legal_state',
  'legal_city',
  'legal_address',
  'legal_zip_code',
];

/**
 * Get Started's Business details (PROD-538): the legal entity and its legal form, typed from
 * scratch; nothing entered at creation is shown, because that is what reached the gateways
 * unchecked. Staff type them — Get Started is not opened to owners (Nishita and Artur,
 * 20 Oct). Once saved, a return visit shows what was typed. Nothing is cleared on the
 * profile until the save.
 *
 * A sole trader may leave the registered name empty and trade under their own name, which
 * the server then stores as the legal name. The legal address is the invoice address; a
 * separate one stays a My info setting.
 */
export default function BusinessDetailsStep({ buttonText, moveForward }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Verification:BusinessDetailsStep' });

  const { company } = useSelector((state) => state.currentCompany);
  const { getValue, updating, pushUpdates } = useBusinessProfile();
  const { setErrorFeedback, renderFeedbackLabel } = useFeedbackLabel({ t });

  // The restaurant's own stamp — the provider's getValue() would read a sibling's through
  // the shared master.
  const hasConfirmed = Boolean(company?.business_profile?.business_details_completed_at);

  const { data, update } = useForm(
    Object.fromEntries(LEGAL_FIELDS.map((field) => [field, hasConfirmed ? (getValue(field) ?? null) : null])),
  );

  const isSoleTrader = data.legal_form === LegalForm.SoleTrader;
  // The state too: AddressFields marks it required, and the server refuses an empty one.
  const isComplete =
    Boolean(data.legal_form) &&
    (isSoleTrader || Boolean(data.legal_name?.trim())) &&
    ['legal_address', 'legal_city', 'legal_zip_code', 'legal_country', 'legal_state'].every((field) =>
      Boolean(data[field]?.trim?.() ?? data[field]),
    );

  // Once per owner (Bohdan, PROD-538): the stamp lands on every restaurant of the group, so a
  // multi-location owner is never asked for one or all — saved for one alone, the fork would
  // leave the master's confirmations behind and send the sibling back here.
  const save = () =>
    isComplete &&
    pushUpdates({
      data: {
        ...data,
        legal_name: data.legal_name?.trim() || null,
        business_details_confirmed: true,
      },
      updateStrategy: BusinessProfileUpdateStrategy.All,
      onSuccess: () => moveForward && moveForward(),
      onError: ({ data: response }) => setErrorFeedback(response),
    });

  return (
    <FadeIn>
      {renderFeedbackLabel}
      <Form layout="vertical">
        <Form.Item label={t('form.legal_form.label')} extra={t('form.legal_form.help')} required>
          <Radio.Group
            value={data.legal_form}
            onChange={(e) => update('legal_form', e.target.value)}
            options={Object.values(LegalForm).map((value) => ({
              value,
              label: t(`form.legal_form.options.${value}`),
            }))}
            data-testid="business-details-legal-form"
          />
        </Form.Item>
        <Form.Item
          label={t('form.legal_name.label')}
          extra={isSoleTrader ? t('form.legal_name.sole_trader_help') : undefined}
          required={!isSoleTrader}
        >
          <Input
            placeholder={t('form.legal_name.placeholder')}
            value={data.legal_name ?? ''}
            onChange={(e) => update('legal_name', e.target.value)}
            data-testid="business-details-legal-name"
          />
        </Form.Item>
        <hr />
        <h6 className="mt-md">{t('sections.address')}</h6>
        <p className="text-gray-500 text-sm">{t('sections.address_help')}</p>
        <AddressFields prefix="legal" data={data} onChange={update} useGrid className="mt-md" />
      </Form>
      <Flex justify="end">
        <Button
          type="primary"
          onClick={save}
          loading={updating}
          disabled={!isComplete}
          data-testid="business-details-save"
        >
          {buttonText ?? t('buttons.next')}
        </Button>
      </Flex>
    </FadeIn>
  );
}
